import { useLocation, useNavigate, useSearchParams } from "react-router"
import { connect, useDispatch, useSelector } from "react-redux"
import { Dispatcher } from "types/redux"
import { GlobalStore, UploadDocStore } from "types/store"
import Loader from "components/Loaders/Loader/Loader"
import { DERMATOSCOPE_VIDEO_PAGE, SURVEY_PAGE } from "core/constants"
import { Button } from "components/Button"
import PreviousButton from "components/Previous/PreviousButton"
import { Buttons, Card } from "components/Card/Card"
import { Reply, Step, UploadDocProps } from "../Survey/types"
import { PATH as SurveyPath } from '../Survey/models';
import actions from "./services/actions"
import { isDermatoDoctorType } from "./services/selector"
import { getCurrentSelectedPatientId } from "../Survey/services/selector"
import { useEffect, useState } from "react"
import { cdn } from "core/cdn"
import styles from "./Upload.module.scss"
import { PATH, UPLOAD_STEPS } from "./models"
import {
  MAIN_PATIENT_ID,
  SMS_LINK,
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_RESOLVED,
} from "./services/constants"
import QRCode from "react-qr-code"
import { openNotification } from "components/Message/Message"
import { useLanguage } from "locales"
import { languages } from "locales/languages"

import actionsSurvey from "../Survey/services/actions"

// Renew the mobile upload link 1 minute before the API expiry so a patient
// never scans a dead QR code. The floor protects against a hot loop if the
// API ever returns an end_at already in the past.
const LINK_REFRESH_MARGIN_MS = 60 * 1000
const MIN_LINK_REFRESH_DELAY_MS = 30 * 1000

// Navigation state accepted by this page. Set by callers outside the survey
// (Profile > Documents importés): the flow then exits to `returnTo` instead
// of the survey.
interface UploadLocationState {
  returnTo?: string
}

const QrCodePage = (props: any): JSX.Element => {
  const { loading, status, current, link, handleCannotScan, standalone, exit, linkPath } = props
  const dispatch = useDispatch();
  const linkCreatedAt = useSelector(({upload}: {upload: UploadDocStore}) => upload.link?.created_at);
  const linkEndAt = useSelector(({upload}: {upload: UploadDocStore}) => upload.link?.end_at);
  const linkReceivedAt = useSelector(({upload}: {upload: UploadDocStore}) => upload.link?.received_at);
  const PENDING = loading && status === STATUS_PENDING
  const documentNavigationLoading = useSelector(
    ({ survey }: GlobalStore) => survey.documentNavigationLoading,
  )
  // Standalone (Profile) has no survey to step back into: leave to `returnTo`.
  const handleBack = () => {
    if (standalone) {
      exit()
    } else {
      dispatch(actionsSurvey.getDocuments("back"))
    }
  }
  // Lifetime = end_at - created_at (API clock) minus the time already elapsed since the link
  // arrived (two kiosk readings, so a kiosk clock running ahead cancels out): no 30 s refresh
  // loop, and no expired QR when the page is re-entered late with the same link.
  // The new link keeps the mode (`linkPath`) of the one it replaces.
  useEffect(() => {
    if (!linkEndAt) return
    const expiresAt = new Date(linkEndAt).getTime()
    if (Number.isNaN(expiresAt)) return
    const issuedAt = linkCreatedAt ? new Date(linkCreatedAt).getTime() : NaN
    const lifetime = Number.isNaN(issuedAt) ? expiresAt - Date.now() : expiresAt - issuedAt
    const elapsed = linkReceivedAt != null ? Date.now() - linkReceivedAt : 0
    const delay = Math.max(lifetime - elapsed - LINK_REFRESH_MARGIN_MS, MIN_LINK_REFRESH_DELAY_MS)
    const timeout = setTimeout(() => {
      dispatch(actions.generateQrCode(linkPath, standalone))
    }, delay)
    return () => clearTimeout(timeout)
  }, [linkCreatedAt, linkEndAt, linkReceivedAt, linkPath, standalone, dispatch])

  return PENDING || documentNavigationLoading ? (
    <Loader />
  ) : (
    <>
      <PreviousButton onClick={handleBack} />
      <Card>
        <h1 className={styles.TitleCustom}>
          {languages.uDocScanQrCode}
          <span style={{ textDecoration: "underline" }}>
            {languages.yourPhonesCamera}
          </span>
          {languages.thenFollowTheSteop}
        </h1>
        <div
          style={{
            height: "auto",
            margin: "0 auto",
            maxWidth: 400,
            width: "100%",
          }}
        >
          {link && (
            <QRCode
              size={256}
              style={{ height: "auto", width: "100%", margin: "auto" }}
              value={link.url}
              viewBox={`0 0 256 256`}
            />
          )}
        </div>
        <Buttons style={{ paddingTop: 30 }}>
          <div
            onClick={(reply) => handleCannotScan(reply)}
            className={styles.webOrMobileButton}
          >
            {current.defaultReply.title}
          </div>
        </Buttons>
      </Card>
    </>
  )
}

// Fallback when the patient cannot scan the QR code: the upload link is sent
// by SMS. Uploading from the kiosk camera is no longer offered, so this page
// no longer needs the dermato flag that used to hide that option.
const SmsLinkPage = (props: any): JSX.Element => {
  const { current, goToStep, handleSmsLinkResponse } = props

  return (
    <>
      <PreviousButton onClick={() => goToStep(PATH.Qrcode)} />
      <Card
        title={current.title}
        subtitle={current.subtitle}
        imgUrl={current.imgUrl}
      >
        <div
          className={styles.UploadFatButton}
          onClick={() => handleSmsLinkResponse(current.replies[0] as Reply)}
        >
          <div className={styles.UploadFatButton__picto}>
            <img src={cdn("images/notifications_phone.svg")} alt="" />
          </div>
          <span style={{ fontSize: "28px", fontWeight: 600 }}>
            {current.replies[0].title}{" "}
          </span>
        </div>
      </Card>
    </>
  )
}

const YourDocHasBeenLoadedSuccessfuly = (props: any): JSX.Element => {
  const { current, status, MobileUpload, exit } = props
  const { languages: lang } = useLanguage()

  if (MobileUpload.STATUS === "finished" || status === STATUS_RESOLVED) {
    const finish = () => {
      props.resetStatus()
      exit()
    }
    return (
      <>
        <PreviousButton onClick={finish} />
        <Card
          title={current.title}
          subtitle={current.subtitle}
          imgUrl={current.imgUrl}
        >
          <Buttons>
            <Button wide="long" type="primary" onClick={finish}>
              {lang.uploadDocumentSuccessButton}
            </Button>
          </Buttons>
        </Card>
      </>
    )
  }
  if (status === STATUS_REJECTED) {
    return (
      <>
        <PreviousButton onClick={exit} />
        <Card
          title={lang.uploadDocumentErrorTitle}
          subtitle={lang.uploadDocumentErrorSubtitle}
          imgUrl={cdn("images/upload_failed.svg")}
        >
          <Buttons>
            <Button wide="long" type="primary" onClick={exit}>
              {lang.uploadDocumentErrorRetryButton}
            </Button>
          </Buttons>
        </Card>
      </>
    )
  }
}

const Upload: React.FC<UploadDocProps> = (props): JSX.Element => {
  const { loading, status, link, MobileUpload, surveyLost } = props
  const [current, setStep] = useState<Step | null>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const uploadPath = searchParams.get("path") || undefined
  const { returnTo } = (location.state as UploadLocationState | null) ?? {}
  // Standalone: started outside the survey (Profile > Documents importés).
  // The document belongs to the account holder and the flow exits to `returnTo`.
  const standalone = Boolean(returnTo)
  const exit = () => navigate(returnTo ?? SURVEY_PAGE)
  // Patient the QR link must be issued for: the account holder outside the
  // survey, the survey's selected patient otherwise.
  const selectedPatientId = useSelector(getCurrentSelectedPatientId)
  const expectedPatientId = standalone ? MAIN_PATIENT_ID : selectedPatientId
  const isDermato = useSelector(isDermatoDoctorType)
  // Dermato flow: entered either through the dermato survey link or a dermatology doctor type.
  const isDermatoFlow = !standalone && (uploadPath === "dermato" || isDermato)
  // Mode of the mobile link: the API signs `is_dermato` in its token for the
  // "dermato" path, which sends the phone to the dermato flow.
  const linkPath = isDermatoFlow ? "dermato" : undefined
  const dispatch = useDispatch()
  const dermatoPatientDetails = useSelector(({ upload }: GlobalStore) => upload?.dermatoPatientDetails)
  const dermatoPatientDetailsReceivedAt = useSelector(
    ({ upload }: GlobalStore) => upload?.dermatoPatientDetailsReceivedAt,
  )
  // receivedAt of the patient details that already moved the survey forward.
  // Details received but not consumed yet (e.g. a poll answered after the
  // patient left the QR page) still trigger the jump on the next QR display;
  // consumed ones do not, so coming back to the QR page after a first
  // submission does not immediately jump to the next step again.
  const dermatoPatientDetailsConsumedReceivedAt = useSelector(
    ({ upload }: GlobalStore) => upload?.dermatoPatientDetailsConsumedReceivedAt,
  )

  // A link kept in the store from an earlier upload is only reused for the
  // patient and mode it was issued for (e.g. not a relative's link from the
  // survey, nor a dermato link, when uploading from the Profile).
  useEffect(() => {
    if (
      !link ||
      link.patientId !== expectedPatientId ||
      Boolean(link.isDermato) !== isDermatoFlow
    ) {
      props.generateQrCode(linkPath, standalone)
    } else {
      setStep(UPLOAD_STEPS[0])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, expectedPatientId, isDermatoFlow])

  useEffect(() => {
    if (surveyLost) exit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyLost])

  // Keep polling for newly uploaded documents while the QR and success screens are
  // shown, so a document added on the phone (e.g. via "Importer un autre document")
  // after the first one is still picked up before the patient confirms and moves on.
  useEffect(() => {
    if (
      link &&
      current &&
      (current.id === PATH.Qrcode || current.id === PATH.Success)
    ) {
      const interval = setInterval(() => {
        props.fetchNewDocuments(link.created_at, standalone)
        if (isDermatoFlow) {
          props.fetchDermatoPatientDetails()
        }
      }, 5000)
      return () => clearInterval(interval)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, link, uploadPath, isDermato, standalone])

  // Move to the success screen the first time a document is detected, without
  // interrupting the polling above.
  // For dermato: skip the "document imported" confirmation screen entirely and
  // jump straight to the next survey step, once the patient's text confirms
  // photos + text were both submitted via "Envoyer" on their phone.
  useEffect(() => {
    if (!current || current.id !== PATH.Qrcode) return
    if (isDermatoFlow) {
      // Already-received dermato details are kept in the store (they feed the
      // recap and the submit payload) and the saga ignores the same document
      // on the next polls, so only details not consumed yet trigger the jump.
      const notConsumedYet =
        dermatoPatientDetailsReceivedAt != null &&
        dermatoPatientDetailsReceivedAt !== dermatoPatientDetailsConsumedReceivedAt
      if (dermatoPatientDetails && notConsumedYet) {
        dispatch(actions.markDermatoPatientDetailsConsumed(dermatoPatientDetailsReceivedAt))
        dispatch(actionsSurvey.jumpTo(SurveyPath.dermato_video_capture))
        navigate(DERMATOSCOPE_VIDEO_PAGE)
      }
    } else {
      if (MobileUpload.STATUS !== undefined) goToStep(PATH.Success)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    current,
    MobileUpload,
    dermatoPatientDetails,
    dermatoPatientDetailsReceivedAt,
    dermatoPatientDetailsConsumedReceivedAt,
    isDermato,
    uploadPath,
  ])

  const goToStep = (stepId) => {
    const indexOfStep = UPLOAD_STEPS.findIndex((step) => step.id === stepId)
    setStep(UPLOAD_STEPS[indexOfStep])
  }

  const handleCannotScan = (r: Reply) => {
    goToStep(PATH.SmsLink)
  }

  const handleSmsLinkResponse = (r: Reply) => {
    if (r.value === SMS_LINK) {
      props.sendMeLink({ url: link?.url as string, isDermato: isDermatoFlow, standalone })
      openNotification({
        type: "info",
        text: languages.uploadDocumentSmsSentNotification,
      })
      goToStep(PATH.Qrcode)
    }
  }
  if (!current) return <Loader />
  switch (current.id) {
    case PATH.Qrcode:
      return (
        <QrCodePage
          {...{
            loading,
            status,
            current,
            link,
            handleCannotScan,
            standalone,
            exit,
            linkPath,
          }}
        />
      )
    case PATH.SmsLink:
      return (
        <SmsLinkPage
          {...{
            current,
            goToStep,
            handleSmsLinkResponse,
          }}
        />
      )
    case PATH.Success:
      return (
        <YourDocHasBeenLoadedSuccessfuly
          {...{
            MobileUpload,
            reset: props.reset,
            resetStatus: props.resetStatus,
            current,
            loading,
            status,
            exit,
          }}
        />
      )
  }
}

const s = (store: GlobalStore) => {
  return { ...store.upload } as UploadDocProps
}

const p = (dispatch: Dispatcher) => ({
  reset: () => dispatch(actions.reset()),
  generateQrCode: (path?: string, standalone?: boolean) =>
    dispatch(actions.generateQrCode(path, standalone)),
  sendMeLink: (link) => dispatch(actions.SendLinkViaSMS(link)),
  fetchNewDocuments: (linkCreatedAt: string | Date, standalone?: boolean) =>
    dispatch(actions.fetchNewDocuments(linkCreatedAt, standalone)),
  fetchDermatoPatientDetails: () => dispatch(actions.fetchDermatoPatientDetails()),
  resetStatus: () => dispatch(actions.resetStatus()),
})

export default connect(s, p)(Upload)
